/**
 * Request/response schemas for the patient portal's identity/session surface (`BACKLOG.md` W3,
 * `docs/decisions/patient-portal-scope.md`).
 *
 * Same discipline as `authRequests.ts`, restated because it matters identically here: every object
 * schema is strict, no server-owned value (an id, a patient identity, a timestamp) appears in any
 * request schema, and the failure vocabulary is a closed union so a caller cannot be told apart by
 * response shape alone. `orgId` is the one field every request here carries that `authRequests.ts`'s
 * staff surface does not need on login: the patient portal is reached in an org-specific context (the
 * practice's own portal entry point), so which practice a registration or login concerns is always
 * known up front, unlike a multi-practice clinician's ambiguous staff login.
 *
 * A separate module from `authRequests.ts`, not an extension of it — this is a structurally different
 * identity surface (`docs/decisions/patient-portal-scope.md`'s own "do not extend the staff session
 * model to patients"), and the two should be able to evolve independently.
 */

import { z } from 'zod';

import { emailSchema, passwordSchema, recoveryCodeSchema, totpCodeSchema } from './authRequests.ts';
import { calendarDateSchema, MAX_CONTACT_LENGTH, MAX_NAME_LENGTH } from './clinicalRequests.ts';

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) => trimmed(max).optional();

/** Six decimal digits — the one-time registration code sent to the patient's on-file contact. Same
 * shape as `authRequests.ts`'s `totpCodeSchema`, a coincidence of format rather than a shared
 * concept: this is a mailed/texted code proving control of a contact channel, not a TOTP step. */
export const registrationCodeSchema = z
  .string()
  .regex(/^\d{6}$/, 'Enter the 6-digit code sent to you');

// --- Registration --------------------------------------------------------------------------------

/**
 * Start self-registration: demographics for matching, plus the password the account will use once
 * verified. No `email`/`phone` here is ever used as the code's delivery target — only as a matching
 * signal against the patient record already on file (`patientAuth/service.ts`'s `startRegistration`).
 */
export const patientPortalRegisterStartRequestSchema = z.strictObject({
  orgId: z.uuid(),
  firstName: trimmed(MAX_NAME_LENGTH),
  lastName: trimmed(MAX_NAME_LENGTH),
  dateOfBirth: calendarDateSchema,
  phone: optionalTrimmed(MAX_CONTACT_LENGTH),
  email: z.email().max(MAX_CONTACT_LENGTH).optional(),
  password: passwordSchema,
});
export type PatientPortalRegisterStartRequest = z.infer<
  typeof patientPortalRegisterStartRequestSchema
>;

/**
 * Always the same shape regardless of what actually happened server-side (confident match, ambiguous
 * match, or no match at all) — `docs/decisions/patient-portal-scope.md`'s no-account-existence-oracle
 * requirement. `registrationId` is opaque and discloses nothing about which branch was taken; it is
 * only a handle the next call needs.
 */
export const patientPortalRegisterStartResponseSchema = z.strictObject({
  registrationId: z.uuid(),
});
export type PatientPortalRegisterStartResponse = z.infer<
  typeof patientPortalRegisterStartResponseSchema
>;

export const patientPortalRegisterVerifyRequestSchema = z.strictObject({
  orgId: z.uuid(),
  registrationId: z.uuid(),
  code: registrationCodeSchema,
});
export type PatientPortalRegisterVerifyRequest = z.infer<
  typeof patientPortalRegisterVerifyRequestSchema
>;

// --- Login / session -------------------------------------------------------------------------------

const mfaSecondFactorFields = {
  totpCode: totpCodeSchema.optional(),
  recoveryCode: recoveryCodeSchema.optional(),
};

export const patientPortalLoginRequestSchema = z.strictObject({
  orgId: z.uuid(),
  email: emailSchema,
  password: passwordSchema,
  ...mfaSecondFactorFields,
});
export type PatientPortalLoginRequest = z.infer<typeof patientPortalLoginRequestSchema>;

/**
 * Who the caller is. Deliberately minimal — this is the identity/session surface only (`BACKLOG.md`
 * W3); records, scheduling, messaging and bill pay (`W2`) are separate, later work that will read
 * `patientId`/`orgId` off this same session but are not built here.
 */
export const patientPortalIdentitySchema = z.strictObject({
  patientAccountId: z.uuid(),
  patientId: z.uuid(),
  orgId: z.uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
});
export type PatientPortalIdentity = z.infer<typeof patientPortalIdentitySchema>;

export const patientPortalSessionResponseSchema = z.strictObject({
  patient: patientPortalIdentitySchema,
});
export type PatientPortalSessionResponse = z.infer<typeof patientPortalSessionResponseSchema>;

// --- TOTP MFA (optional for v1) ---------------------------------------------------------------------

export const patientPortalMfaEnrollResponseSchema = z.strictObject({
  secret: z.string(),
  provisioningUri: z.string(),
});
export type PatientPortalMfaEnrollResponse = z.infer<typeof patientPortalMfaEnrollResponseSchema>;

export const patientPortalMfaEnrollConfirmRequestSchema = z.strictObject({
  code: totpCodeSchema,
});
export type PatientPortalMfaEnrollConfirmRequest = z.infer<
  typeof patientPortalMfaEnrollConfirmRequestSchema
>;

export const patientPortalMfaEnrollConfirmResponseSchema = z.strictObject({
  recoveryCodes: z.array(z.string()),
});
export type PatientPortalMfaEnrollConfirmResponse = z.infer<
  typeof patientPortalMfaEnrollConfirmResponseSchema
>;

// --- Errors ------------------------------------------------------------------------------------------

/**
 * Closed failure vocabulary, mirroring `authRequests.ts`'s `AUTH_ERROR_CODES` reasoning exactly:
 * `invalid_credentials` covers no-such-account, wrong password, and a deactivated account in one
 * code, and `invalid_or_expired_registration` covers a registration that never existed, already
 * expired, was already consumed, or never had a real code at all (the decoy branches
 * `startRegistration` creates for a failed/ambiguous match) — distinguishing any of those would
 * reopen exactly the account-existence oracle `docs/decisions/patient-portal-scope.md` requires this
 * surface to close at every step.
 */
export const PATIENT_PORTAL_ERROR_CODES = [
  'invalid_request',
  'invalid_credentials',
  'mfa_required',
  'invalid_totp_code',
  'mfa_already_enabled',
  'invalid_or_expired_registration',
  'unauthenticated',
  'audit_unavailable',
  'rate_limited',
] as const;
export type PatientPortalErrorCode = (typeof PATIENT_PORTAL_ERROR_CODES)[number];

export function isPatientPortalErrorCode(value: string): value is PatientPortalErrorCode {
  return (PATIENT_PORTAL_ERROR_CODES as readonly string[]).includes(value);
}

export const patientPortalErrorResponseSchema = z.strictObject({
  error: z.enum(PATIENT_PORTAL_ERROR_CODES),
});
export type PatientPortalErrorResponse = z.infer<typeof patientPortalErrorResponseSchema>;

/** Same "MFA required" carve-out `authRequests.ts`'s `mfaRequiredResponseSchema` argues for: safe to
 * disclose only because it is returned after the caller has already proven their password. */
export const patientPortalMfaRequiredResponseSchema = z.strictObject({
  error: z.literal('mfa_required'),
});
export type PatientPortalMfaRequiredResponse = z.infer<
  typeof patientPortalMfaRequiredResponseSchema
>;

export const patientPortalOkResponseSchema = z.strictObject({ ok: z.literal(true) });
export type PatientPortalOkResponse = z.infer<typeof patientPortalOkResponseSchema>;

// --- Staff review queue (patient-facing schemas above; this is the staff-facing surface) -----------

/** Approve a pending registration review: link it to a specific patient record. Staff-only,
 * `patientAuth/routes.ts`'s `POST /patient-portal/registration-reviews/:reviewId/approve`. */
export const approveRegistrationReviewRequestSchema = z.strictObject({
  patientId: z.uuid(),
});
export type ApproveRegistrationReviewRequest = z.infer<
  typeof approveRegistrationReviewRequestSchema
>;

/** The plaintext one-time code, shown to staff exactly once — see that route's own comment for why. */
export const approveRegistrationReviewResponseSchema = z.strictObject({
  code: z.string(),
});
export type ApproveRegistrationReviewResponse = z.infer<
  typeof approveRegistrationReviewResponseSchema
>;
